import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function elapsed(startedAt, now) {
  const ms = now - new Date(startedAt).getTime();
  const m  = Math.floor(ms / 60000);
  const h  = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

// ─── Live Production Board ────────────────────────────────────────────────────
// showForceOff: true for management view, false for read-only shop floor view
export default function LiveBoard({ showForceOff = false, compact = false }) {
  const [sessions,    setSessions]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [now,         setNow]         = useState(Date.now());
  const [forcingOff,  setForcingOff]  = useState(null);
  const [msg,         setMsg]         = useState(null);

  const load = useCallback(() => {
    api.get('/clock-sessions')
      .then(r => { setSessions(r.data.value || []); setLastRefresh(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Refresh every 60 s; update elapsed every 30 s
  useEffect(() => {
    load();
    const refresh = setInterval(load, 60000);
    const tick    = setInterval(() => setNow(Date.now()), 30000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [load]);

  const forceOff = async (session) => {
    if (!window.confirm(`Force clock-off ${session.staff_name}?`)) return;
    setForcingOff(session.id);
    try {
      const r = await api.delete(`/clock-sessions/${session.id}`);
      setMsg({ text: `${r.data.staff_name} clocked off — ${r.data.hours_logged.toFixed(2)}h logged`, ok: true });
      load();
    } catch (e) {
      setMsg({ text: e.response?.data?.error || e.message, ok: false });
    }
    setForcingOff(null);
    setTimeout(() => setMsg(null), 4000);
  };

  const labelStyle = {
    fontFamily: 'IBM Plex Mono, monospace', fontSize: 9,
    color: '#484f58', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2,
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 16, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#e6edf3' }}>
            Live Production Board
          </div>
          {lastRefresh && (
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', marginTop: 2 }}>
              Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · auto-refresh 60s
            </div>
          )}
        </div>
        <button onClick={load} style={{
          padding: '6px 14px', background: '#1c2333', border: '1px solid #30363d',
          borderRadius: 6, color: '#8b949e', cursor: 'pointer',
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
        }}>
          Refresh
        </button>
      </div>

      {/* Toast */}
      {msg && (
        <div style={{
          background: msg.ok ? 'rgba(63,185,80,0.1)' : 'rgba(248,81,73,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(63,185,80,0.4)' : 'rgba(248,81,73,0.4)'}`,
          color: msg.ok ? '#3fb950' : '#f85149',
          borderRadius: 6, padding: '10px 14px', marginBottom: 14,
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
        }}>
          {msg.ok ? '✓ ' : '✗ '}{msg.text}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#484f58', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, letterSpacing: 2 }}>
          Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#484f58', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, letterSpacing: 2 }}>
          No staff currently clocked on
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 12 }}>
          {sessions.map(s => (
            <div key={s.id} style={{
              background: '#1c2333', border: '1px solid #30363d', borderRadius: 8,
              padding: compact ? '12px 14px' : '16px',
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
            }}>
              <div>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: compact ? 16 : 18, fontWeight: 700, marginBottom: 4 }}>
                  {s.staff_name}
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
                  <div>
                    <div style={labelStyle}>Work Centre</div>
                    <div style={{ fontSize: 13, color: '#f0a500', fontFamily: 'IBM Plex Mono, monospace' }}>{s.work_centre_name}</div>
                  </div>
                  {s.non_productive ? (
                    <div>
                      <div style={labelStyle}>Activity</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ background: 'rgba(88,166,255,0.12)', color: '#58a6ff', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 3, padding: '1px 6px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>NON-PROD</span>
                        {s.description && <span style={{ fontSize: 12, color: '#8b949e' }}>{s.description}</span>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={labelStyle}>Job</div>
                      <div style={{ fontSize: 13 }}>
                        <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#f0a500' }}>{s.job_number}</span>
                        {s.project_title && <span style={{ color: '#8b949e', marginLeft: 6 }}>— {s.project_title}</span>}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={labelStyle}>Elapsed</div>
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: '#e6edf3' }}>
                      {elapsed(s.started_at, now)}
                    </div>
                  </div>
                </div>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58' }}>
                  Clocked on {new Date(s.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              {showForceOff && (
                <button
                  onClick={() => forceOff(s)}
                  disabled={forcingOff === s.id}
                  style={{
                    padding: '10px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                    background: forcingOff === s.id ? '#1c2333' : 'rgba(248,81,73,0.15)',
                    color: '#f85149', border: '1px solid rgba(248,81,73,0.3)',
                    fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
                    opacity: forcingOff === s.id ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}
                >
                  {forcingOff === s.id ? '…' : 'Force Off'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', textAlign: 'right' }}>
        {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} active
      </div>
    </div>
  );
}
